import React, { useEffect, useState } from "react";
import { IconArrowLeft } from "@velaro/velaro-component-lib/";
import { PrimaryBlueButton } from "@velaro/velaro-component-lib";
import useApi from "@/hooks/useApi";
import { useLocation } from "react-router-dom";
import { IconPlus } from "@velaro/velaro-component-lib/";
const SecurityLogo = "/img/velaro_security-safe-encryption.png";
const IntegrationCompleteLogo = "/img/velaro_integrations-complete.png";
import NetsuitePanel from "./NetSuitePanel";
import { useNavigate } from "react-router-dom";
import NetSuiteAccounts from "./NetSuiteAccounts";

interface Props {
  appStatus?: string;
}

interface NetSuiteConfigById {
  id: number;
  displayName: string;
  company: string;
  consumerKey: string;
  consumerSecret: string;
  teamId: number;
  createdAt: string;
  token: string;
  tokenSecret: string;
  restletUrl: string;
}

interface TestConnectionResult {
  authValid: boolean;
  hasCustomerAccess: boolean;
  hasSupportCaseAccess: boolean;
  error?: string;
}

export default function NetSuiteCreate(props: Props) {
  const [displayName, setDisplayName] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [consumerKey, setConsumerKey] = useState("");
  const [consumerSecret, setConsumerSecret] = useState("");
  const [token, setToken] = useState("");
  const [tokenSecret, setTokenSecret] = useState("");
  const [restletUrl, setRestletUrl] = useState("");
  const [pushTranscripts, setPushTranscripts] = useState(false);
  const [transcriptFieldName, setTranscriptFieldName] = useState("custentity_transcript");
  const maxLength = 108;
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [testing, setTesting] = useState(false);
  const api = useApi();
  const location = useLocation();
  const { teamId, appStatus } = location.state || {};
  const [configId, setConfigId] = useState<number>(-1);
  const [step, setStep] = useState(appStatus === "Pending" ? 3 : 1);
  const [panels, setPanels] = useState<{ id: number; metadataType: string }[]>(
    []
  );
  const [showSaved, setShowSaved] = useState(false);
  const [config, setConfig] = useState<NetSuiteConfigById | null>(null);
  const navigate = useNavigate();
  const addPanel = () =>
    setPanels([...panels, { id: panels.length + 1, metadataType: "" }]);

  useEffect(() => {
    if (step === 3 && panels.length === 0) {
      addPanel();
    }
  }, [step]);

  useEffect(() => {
    async function fetchNetSuiteConfig() {
      if (!configId) return;

      try {
        const resp = await api.messaging.get(
          `NetSuite/Integrations/${configId}`
        );
        if (resp.ok) {
          const data: NetSuiteConfigById[] = await resp.json();
          if (data.length > 0) {
            setConfig(data[0]);
          } else {
            console.warn("No config data found for the given ID");
          }
        } else {
          console.error("Failed to fetch NetSuite config");
        }
      } catch (error) {
        console.error("Error fetching NetSuite config:", error);
      }
    }

    fetchNetSuiteConfig();
  }, [configId]);

  useEffect(() => {
    if (config && appStatus !== "Connected") {
      setDisplayName(config.displayName || "");
      setCompanyId(config.company || "");
      setConsumerKey(config.consumerKey || "");
      setConsumerSecret(config.consumerSecret || "");
      setToken(config.token || "");
      setTokenSecret(config.tokenSecret || "");
      setRestletUrl(config.restletUrl || "");
    }
  }, [config, appStatus]);

  useEffect(() => {
    if (appStatus === "Pending") {
      setStep(3);
    }
  }, [appStatus]);

  const showSavedMessage = () => {
    setShowSaved(true);
    setTimeout(() => setShowSaved(false), 3000);
  };

  async function handleSubmit() {
    setSending(true);
    const response = await api.messaging.post("NetSuite/Auth/SaveCredentials", {
      displayName: displayName,
      company: companyId,
      consumerKey: consumerKey,
      consumerSecret: consumerSecret,
      token: token,
      tokenSecret: tokenSecret,
      restletUrl: restletUrl,
      teamId: teamId,
      pushTranscripts: pushTranscripts,
      transcriptFieldName: transcriptFieldName || "custentity_transcript"
    });
    const result = await response.json();

    setSuccess(response.ok);
    if (response.ok && result.id) {
      setConfigId(result.id);
      setStep(step + 1);
    }
    setSending(false);
  }

  const isNextActive =
    displayName && companyId && consumerKey && consumerSecret && token && tokenSecret;

  const nextBtnClass = !isNextActive
    ? "bg-slate-300 border-slate-300 hover:bg-slate-300 hover:border-slate-500 text-white cursor-not-allowed"
    : "";

  return (
    <div className="panel bg-white dark:bg-stone-300 rounded-2xl shadow-md mb-4 p-6 xl:max-w-[1350px] xl:justify-center xl:mx-auto">
      <div className="w-full justify-between items-center inline-flex">
        <div className="w-full rounded-lg justify-between items-center gap-2 flex">
          <div className="text-center text-slate-500 text-sm font-medium flex">
            {step > 1 ? (
              <button
                className="btn flex items-center gap-2"
                onClick={() => setStep(step - 1)}
              >
                {React.createElement(IconArrowLeft)}Back
              </button>
            ) : (
              <button
                className="btn flex items-center gap-2"
                onClick={() => navigate("/integrations")}
              >
                {React.createElement(IconArrowLeft)}Back
              </button>
            )}
          </div>
          {step < 4 && (
            <PrimaryBlueButton
              label="Next"
              onClick={async () => {
                if (isNextActive) {
                  await handleSubmit();
                }
              }}
              className={nextBtnClass}
              disabled={!isNextActive || sending}
            />
          )}
        </div>
      </div>

      <div className="flex items-center w-full mt-4">
        {[
          "Authentication",
          "Connection Success",
          "Field Mapping",
          "Completed"
        ].map((label, index) => {
          const isActive = step >= index + 1;
          const isCurrent = step === index + 1;
          const isCompleted = step > index + 1;
          return (
            <React.Fragment key={index}>
              <div className="flex flex-col items-center">
                <div
                  className={`w-8 h-8 flex items-center justify-center rounded-full border-2 z-10 ${
                    isActive ? "border-cornflower-blue-500" : "border-gray-300"
                  } bg-white`}
                >
                  <div
                    className={`w-6 h-6 rounded-full z-10 ${
                      isActive ? "bg-cornflower-blue-500" : "bg-gray-300"
                    }`}
                  ></div>
                </div>
                <p
                  className={`mt-2 font-medium text-sm ${
                    isCurrent
                      ? "text-cornflower-blue-500"
                      : isCompleted
                      ? "text-slate-800"
                      : "text-gray-400"
                  }`}
                >
                  {label}
                </p>
              </div>
              {index < 3 && (
                <div
                  className={`flex-1 border-t-4 mb-8 -mx-[51px] z-0 ${
                    step > index + 1
                      ? "border-cornflower-blue-500 "
                      : step === index + 1
                      ? "border-cornflower-blue-500 border-dashed"
                      : "border-gray-300 border-dashed"
                  }`}
                ></div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && (
        <div className="w-1/3 place-self-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <div className="text-center text-slate-900 text-xl font-medium">
              Netsuite Configuration
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Display Name
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-white rounded-lg border border-slate-300"
                value={displayName}
                placeholder="Enter display name"
                onChange={(e) => setDisplayName(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                NetSuite Account ID
              </label>
              <input
                type="text"
                className="px-4 py-2 bg-white rounded-lg border border-slate-300"
                value={companyId}
                placeholder="e.g. 1234567 (found in NetSuite → Setup → Company → Company Information)"
                onChange={(e) => setCompanyId(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Consumer Key
              </label>
              <input
                type="text"
                className={`px-4 py-2 bg-white rounded-lg border transition-colors duration-200 border-slate-300 focus:border-cornflower-blue-500`}
                value={consumerKey}
                placeholder="Enter consumer key"
                onChange={(e) => setConsumerKey(e.target.value)}
                required
              />
              <div className="text-xs text-right">
                <span className="text-slate-500">{consumerKey.length}</span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Consumer Secret
              </label>
              <input
                type="text"
                className={`px-4 py-2 bg-white rounded-lg border transition-colors duration-200 border-slate-300 focus:border-cornflower-blue-500`}
                value={consumerSecret}
                placeholder="Enter consumer secret"
                onChange={(e) => setConsumerSecret(e.target.value)}
                required
              />
              <div className="text-xs text-right">
                <span className="text-slate-500">{consumerSecret.length}</span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Token
              </label>
              <input
                type="text"
                className={`px-4 py-2 bg-white rounded-lg border transition-colors duration-200 border-slate-300 focus:border-cornflower-blue-500`}
                value={token}
                placeholder="Enter token"
                onChange={(e) => setToken(e.target.value)}
                required
              />
              <div className="text-xs text-right">
                <span className="text-slate-500">{token.length}</span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Token Secret
              </label>
              <input
                type="text"
                className={`px-4 py-2 bg-white rounded-lg border transition-colors duration-200 border-slate-300 focus:border-cornflower-blue-500`}
                value={tokenSecret}
                placeholder="Enter token secret"
                onChange={(e) => setTokenSecret(e.target.value)}
                required
              />
              <div className="text-xs text-right">
                <span className="text-slate-500">{tokenSecret.length}</span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-slate-800 text-sm font-medium">
                Restlet URL <span className="text-slate-400 font-normal text-xs">(optional)</span>
              </label>
              <p className="text-slate-500 text-xs">
                Only needed for the <code>netsuite_restlet</code> AI tool (custom SuiteScript calls). Leave blank if you are not using custom scripts — all 23 other AI tools work without it.
              </p>
              <input
                type="text"
                className="px-4 py-2 bg-white rounded-lg border border-slate-300"
                value={restletUrl}
                placeholder="https://your-account.restlets.api.netsuite.com/app/site/hosting/restlet.nl?script=...&deploy=1"
                onChange={(e) => setRestletUrl(e.target.value)}
              />
            </div>

            {/* Required NetSuite permissions */}
            <details className="border border-slate-200 rounded-lg px-4 py-3 text-sm">
              <summary className="cursor-pointer font-medium text-slate-700 select-none">
                What permissions does my NetSuite role need?
              </summary>
              <ul className="mt-3 space-y-1 text-slate-600 text-xs list-disc list-inside">
                <li>Setup → REST Web Services: Full</li>
                <li>Setup → SuiteQL: Full</li>
                <li>Setup → Token-Based Authentication: Full</li>
                <li>Transactions → Sales Orders: View, Create, Edit</li>
                <li>Transactions → Return Authorizations: View, Create</li>
                <li>Transactions → Invoices: View</li>
                <li>Lists → Customers: View, Create, Edit</li>
                <li>Lists → Items: View</li>
                <li>Support → Cases: View, Create, Edit</li>
              </ul>
              <p className="mt-3 text-xs text-slate-500">
                Easiest setup: copy the built-in <strong>Full Access</strong> role in NetSuite, rename it{" "}
                <strong>Velaro Integration</strong>, and assign it to your token user.
              </p>
            </details>

            {/* Transcript push settings */}
            <div className="flex flex-col gap-3 border-t border-slate-200 pt-4">
              <div className="text-slate-800 text-sm font-semibold">Transcript Push</div>
              <p className="text-slate-500 text-xs">
                When enabled, Velaro automatically pushes the full chat transcript to the matched
                NetSuite customer record as a custom field update on every conversation resolve.
                The customer is matched by the conversation contact's email address.
              </p>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-blue-500"
                  checked={pushTranscripts}
                  onChange={(e) => setPushTranscripts(e.target.checked)}
                />
                <span className="text-slate-700 text-sm">Push transcripts to NetSuite on conversation resolve</span>
              </label>

              {pushTranscripts && (
                <div className="flex flex-col gap-1">
                  <label className="text-slate-700 text-sm font-medium">
                    NetSuite Field API Name
                  </label>
                  <input
                    type="text"
                    className="px-4 py-2 bg-white rounded-lg border border-slate-300 text-sm"
                    value={transcriptFieldName}
                    placeholder="custentity_transcript"
                    onChange={(e) => setTranscriptFieldName(e.target.value)}
                  />
                  <p className="text-slate-400 text-xs">
                    The API name of the custom field on the Customer record to write the transcript into.
                    Find it in: <strong>NetSuite → Customization → Lists, Records & Fields → Entity Fields</strong>.
                    Default: <code>custentity_transcript</code>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="w-1/3 place-self-center text-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img
              className="w-40 place-self-center"
              src={SecurityLogo}
              alt=""
            ></img>
            <div className="text-center text-slate-800 text-xl font-medium">
              Congratulations! Successful Integration Connection
            </div>
            <p className="text-center text-slate-800 text-sm font-medium">
              Enjoy a seamless flow of data between Velaro and the Netsuite.
              <br />
              Take the next step to tailor your integration by configuring
              settings.
            </p>

            {/* Test Connection */}
            <div className="text-left border border-slate-200 rounded-lg p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-slate-700 text-sm font-medium">Verify your credentials</span>
                <button
                  type="button"
                  disabled={testing || configId <= 0}
                  onClick={async () => {
                    setTesting(true);
                    setTestResult(null);
                    try {
                      const resp = await api.messaging.post(
                        `NetSuite/Integrations/${configId}/TestConnection`
                      );
                      const data: TestConnectionResult = await resp.json();
                      setTestResult(data);
                    } catch {
                      setTestResult({ authValid: false, hasCustomerAccess: false, hasSupportCaseAccess: false, error: "Request failed. Please try again." });
                    } finally {
                      setTesting(false);
                    }
                  }}
                  className="px-4 py-1.5 text-sm font-medium rounded-lg border border-cornflower-blue-500 text-cornflower-blue-500 hover:bg-cornflower-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testing ? "Testing…" : "Test Connection"}
                </button>
              </div>

              {testResult && (
                <div className="flex flex-col gap-1.5 text-sm">
                  {[
                    { label: "OAuth Authentication", ok: testResult.authValid },
                    { label: "Customer Record Access", ok: testResult.hasCustomerAccess },
                    { label: "Support Case Access", ok: testResult.hasSupportCaseAccess },
                  ].map(({ label, ok }) => (
                    <div key={label} className="flex items-center gap-2">
                      <span className={ok ? "text-green-600" : "text-red-500"}>{ok ? "✓" : "✗"}</span>
                      <span className={ok ? "text-slate-700" : "text-slate-500"}>{label}</span>
                    </div>
                  ))}
                  {testResult.error && (
                    <p className="text-red-600 text-xs mt-1">{testResult.error}</p>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-center">
              <button
                type="submit"
                className="rounded-lg items-center px-6 py-2 bg-white rounded-lg border border-cornflower-cornflower-blue-500 hover:border-cornflower-blue-400 text-center text-cornflower-cornflower-blue-500 text-sm font-medium"
                onClick={() => navigate("/integrations/")}
              >
                Finish Later
              </button>
              <PrimaryBlueButton
                label="Set up Integration"
                onClick={() => {
                  setStep(step + 1);
                }}
                className=""
              />
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="w-full py-8">
          <div className="w-full pb-8 flex gap-2">
            <NetSuiteAccounts
              configs={config ? [config] : []}
              deleteConfig={() => {}}
              addConfig={() => {}}
              teamId={teamId!}
              onChange={() => {}}
              onSelectConfig={() => {}}
              onCreateMode={true}
            />
          </div>
          <hr className="py-4"></hr>
          <div className="flex justify-between">
            <div>
              <div className="text-slate-800 text-base font-medium">
                Choose which records to sync
              </div>
              <div className="text-slate-500 text-sm font-normal">
                Mappings connect individual fields with matching information
                from each app.{" "}
              </div>
            </div>
            <PrimaryBlueButton
              label="Add Mapping"
              onClick={addPanel}
              className=""
              prefixIcon={React.createElement(IconPlus)}
            />
          </div>

          <div className="mt-4">
            {panels.map((panel) => (
              <NetsuitePanel
                key={panel.id}
                teamId={teamId!}
                metadataType={panel.metadataType}
                showSavedMessage={showSavedMessage}
                isCreateMode={true}
                selectedConfigId={configId}
                isDeleteDisabled={panels.length === 1}
              />
            ))}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="w-1/3 place-self-center text-center">
          <div className="w-full py-8 flex-col gap-6 inline-flex">
            <img
              className="w-40 place-self-center"
              src={IntegrationCompleteLogo}
              alt=""
            ></img>
            <div className="text-center text-slate-800 text-xl font-medium">
              Completed Integration!
            </div>
            <p className="text-center text-slate-800 text-sm font-normal">
              Your integration is successfully set up and ready to use. <br />
              All connected features are now active and syncing as expected.
            </p>

            <div className="flex gap-2 justify-center">
              <button
                type="button"
                className="rounded-lg items-center px-6 py-2 bg-white rounded-lg border border-cornflower-cornflower-blue-500 hover:border-cornflower-blue-400 text-center text-cornflower-cornflower-blue-500 text-sm font-medium"
                onClick={() => navigate("/integrations/")}
              >
                Go to Dashboard
              </button>
              <PrimaryBlueButton
                label="View Configuration"
                onClick={() =>
                  (window.location.href = "/integrations/netsuite")
                }
                className=""
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
